import React, { useState, useMemo } from "react";
import { ChevronIcon } from "../icons";
import { fmtDate, fmtDateFull, fmtMonth } from "../utils/helpers";
import S from "../styles";

const JOB_TYPE_COLORS = { "Laser Cutting": "#ef4444", "3D Printing": "#8b5cf6", "UV Printing": "#3b82f6", CNC: "#f59e0b" };

// ─── Icons ────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

// ─── Job Detail View ──────────────────────────────────
function JobDetail({ job, employees, onBack }) {
  const employee = job.selectedEmployee ? employees.find((e) => String(e.id) === String(job.selectedEmployee)) : null;
  const margin = job.customerCost > 0 ? (job.profit / job.customerCost) * 100 : 0;
  const laborCost = parseFloat(job.laborHours || 0) * parseFloat(job.laborRate || 0);

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "rgba(10,10,15,0.58)", fontSize: 15, cursor: "pointer", fontFamily: "'Archivo', sans-serif", marginBottom: 20, padding: 0 }}
      >
        <BackIcon /> All Jobs
      </button>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <h2 style={{ fontSize: 26, fontWeight: 700, color: "#0A0A0F" }}>{job.jobNumber || "—"}</h2>
            <span style={{
              display: "inline-block", padding: "3px 12px", borderRadius: 0, fontSize: 13, fontWeight: 600,
              background: "#0c3a2a", color: "#34d399", border: "1px solid #34d39930",
            }}>
              Completed
            </span>
            {job.jobType && (
              <span style={{
                display: "inline-block", padding: "3px 12px", borderRadius: 0, fontSize: 13, fontWeight: 600,
                background: `${JOB_TYPE_COLORS[job.jobType] || "rgba(10,10,15,0.58)"}20`, color: JOB_TYPE_COLORS[job.jobType] || "rgba(10,10,15,0.58)", border: `1px solid ${JOB_TYPE_COLORS[job.jobType] || "rgba(10,10,15,0.58)"}30`,
              }}>
                {job.jobType}
              </span>
            )}
          </div>
          <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>
            Completed on {job.date ? fmtDateFull(job.date) : "—"}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}>
          <span style={S.statLabel}>Customer Cost</span>
          <span style={{ ...S.statValue, color: "#0033FF" }}>${job.customerCost.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Our Cost</span>
          <span style={S.statValue}>${job.ourCost.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Net Profit</span>
          <span style={{ ...S.statValue, color: "#34d399" }}>${job.profit.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Margin</span>
          <span style={S.statValue}>{margin.toFixed(1)}%</span>
        </div>
      </div>

      <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Customer Info */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Customer</span>
          </div>
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <InfoRow label="Name" value={job.customer} />
            <InfoRow label="Email" value={job.customerEmail} />
            <InfoRow label="Phone" value={job.customerPhone} />
          </div>
        </div>

        {/* Labor Info */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Labor</span>
          </div>
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <InfoRow label="Employee" value={employee ? `${employee.name} — ${employee.position}` : null} />
            <InfoRow label="Hours" value={job.laborHours ? `${job.laborHours} hrs` : null} />
            <InfoRow label="Rate" value={job.laborRate ? `$${parseFloat(job.laborRate).toFixed(2)}/hr` : null} />
            <InfoRow label="Labor Total" value={laborCost > 0 ? `$${laborCost.toLocaleString()}` : null} highlight />
          </div>
        </div>
      </div>

      {/* Line Items */}
      {job.lineItems && job.lineItems.length > 0 && job.lineItems[0].type && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 12 }}>
            Material Line Items
            <span style={{ ...S.jobCountBadge, marginLeft: 10 }}>{job.lineItems.length}</span>
          </h3>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "6%" }}>#</th>
                  <th style={{ ...S.th, width: "16%" }}>Material</th>
                  <th style={{ ...S.th, width: "18%" }}>Color / Variant</th>
                  <th style={{ ...S.th, width: "14%" }}>Sheet Size</th>
                  <th style={{ ...S.th, width: "12%" }}>Thickness</th>
                  <th style={{ ...S.th, width: "12%", textAlign: "right" }}>Parts/Sheet</th>
                  <th style={{ ...S.th, width: "12%", textAlign: "right" }}>Total Parts</th>
                  <th style={{ ...S.th, width: "10%", textAlign: "right" }}>Sheets</th>
                </tr>
              </thead>
              <tbody>
                {job.lineItems.map((li, idx) => {
                  const sheets = li.partsPerSheet > 0 ? Math.ceil(li.partsNeeded / li.partsPerSheet) : 0;
                  return (
                    <tr key={li.id || idx}>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{idx + 1}</td>
                      <td style={S.td}><span style={S.typeBadge}>{li.type}</span></td>
                      <td style={{ ...S.td, color: "#0A0A0F" }}>{li.color}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{li.sheetSize}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{li.thickness}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>{li.partsPerSheet}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>{li.partsNeeded}</td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0033FF", fontWeight: 600 }}>{sheets}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost Summary Bar */}
      <div style={{ ...S.grandTotalBar, marginTop: 0 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "#FFFFFF", opacity: 0.7 }}>Total Job Cost</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "#FFFFFF" }}>${job.ourCost.toLocaleString()}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "#FFFFFF", opacity: 0.7 }}>Customer Paid</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "#FFFFFF" }}>${job.customerCost.toLocaleString()}</div>
        </div>
      </div>
      <div style={{ ...S.profitBar, marginTop: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "#0c3a2a", opacity: 0.7 }}>Net Profit</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "#0c3a2a" }}>${job.profit.toLocaleString()}</div>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 800, color: "#0c3a2a" }}>
          {margin.toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, color: value ? (highlight ? "#0033FF" : "#0A0A0F") : "rgba(10,10,15,0.58)", fontFamily: highlight ? "'JetBrains Mono', monospace" : "inherit", fontWeight: highlight ? 600 : 400 }}>{value || "—"}</div>
    </div>
  );
}

// ─── Job List View ────────────────────────────────────
export default function JobHistoryPage({ jobs, employees = [] }) {
  const [expandedMonths, setExpandedMonths] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);

  const grouped = useMemo(() => {
    const map = {};
    const sorted = [...jobs].sort((a, b) => b.date.localeCompare(a.date));
    for (const j of sorted) {
      const k = j.date.slice(0, 7);
      if (!map[k]) map[k] = [];
      map[k].push(j);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [jobs]);

  const toggle = (k) => setExpandedMonths((p) => ({ ...p, [k]: !p[k] }));

  const allProfit = jobs.reduce((s, j) => s + j.profit, 0);
  const allRev = jobs.reduce((s, j) => s + j.customerCost, 0);
  const allCost = jobs.reduce((s, j) => s + j.ourCost, 0);
  const avgM = allRev > 0 ? (allProfit / allRev) * 100 : 0;

  if (selectedJob) {
    return <JobDetail job={selectedJob} employees={employees} onBack={() => setSelectedJob(null)} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Job History</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>View completed jobs and historical performance</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 28 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Jobs</span><span style={S.statValue}>{jobs.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Revenue</span><span style={S.statValue}>${allRev.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Costs</span><span style={S.statValue}>${allCost.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Profit</span><span style={{ ...S.statValue, color: "#34d399" }}>${allProfit.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Avg Margin</span><span style={S.statValue}>{avgM.toFixed(1)}%</span></div>
      </div>
      {grouped.map(([mk, mj]) => {
        const isOpen = expandedMonths[mk] !== false;
        const mp = mj.reduce((s, j) => s + j.profit, 0);
        const mr = mj.reduce((s, j) => s + j.customerCost, 0);
        const mm = mr > 0 ? (mp / mr) * 100 : 0;
        return (
          <div key={mk} style={{ marginBottom: 16 }}>
            <button onClick={() => toggle(mk)} style={S.monthHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>{fmtMonth(mk)}</span>
                <span style={S.jobCountBadge}>{mj.length} job{mj.length !== 1 ? "s" : ""}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Revenue</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#0A0A0F" }}>${mr.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Profit</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#34d399" }}>${mp.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: "right", minWidth: 50 }}>
                  <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Margin</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#0033FF" }}>{mm.toFixed(0)}%</div>
                </div>
              </div>
            </button>
            {isOpen && (
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "11%" }}>Job #</th>
                      <th style={{ ...S.th, width: "10%" }}>Date</th>
                      <th style={{ ...S.th, width: "20%" }}>Customer</th>
                      <th style={{ ...S.th, width: "13%" }}>Job Type</th>
                      <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Customer Cost</th>
                      <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Our Cost</th>
                      <th style={{ ...S.th, width: "18%", textAlign: "right" }}>Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mj.map((j) => {
                      const mg = j.customerCost > 0 ? (j.profit / j.customerCost) * 100 : 0;
                      return (
                        <tr
                          key={j.id}
                          onClick={() => setSelectedJob(j)}
                          style={{ cursor: "pointer", transition: "background .1s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22,45,80,0.4)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={S.td}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0033FF", fontWeight: 600 }}>
                              {j.jobNumber || "—"}
                            </span>
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "rgba(10,10,15,0.58)" }}>{fmtDate(j.date)}</td>
                          <td style={{ ...S.td, fontWeight: 500, color: "#0A0A0F" }}>{j.customer}</td>
                          <td style={S.td}>
                            {j.jobType ? (
                              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 0, fontSize: 13, fontWeight: 600, background: `${JOB_TYPE_COLORS[j.jobType] || "rgba(10,10,15,0.58)"}20`, color: JOB_TYPE_COLORS[j.jobType] || "rgba(10,10,15,0.58)", border: `1px solid ${JOB_TYPE_COLORS[j.jobType] || "rgba(10,10,15,0.58)"}30` }}>{j.jobType}</span>
                            ) : <span style={{ color: "rgba(10,10,15,0.58)" }}>—</span>}
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>${j.customerCost.toLocaleString()}</td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>${j.ourCost.toLocaleString()}</td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                              <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "2px 8px", borderRadius: 0}}>{mg.toFixed(0)}%</span>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#34d399" }}>${j.profit.toLocaleString()}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
